import { IImage } from '@models/IImage';
import { IVideo } from '@models/IVideo';
import { VideoButton } from '@molecules/VideoButton/VideoButton';
import useDevicePixelRatio from '@utility/hooks/useDevicePixelRatio';
import classNames from 'classnames';
import { useRef, useState } from 'react';

interface IHeroMedia {
  alternativeText?: string;
  autoPlay?: boolean;
  className: string;
  darkFont?: boolean;
  hasQBrickPlayButton?: boolean;
  image?: IImage;
  isSplit: boolean;
  mobileImage?: IImage;
  muted?: boolean;
  playButtonLabel: string;
  video?: IVideo;
  videoPoster?: IImage;
  videoConfig?: string;
}

const HeroImageComponent = ({
  isSplit,
  mobileImage,
  src,
}: {
  isSplit: boolean;
  mobileImage?: IImage;
  src: IImage;
}) => {
  const devicePixelRatio = useDevicePixelRatio() ?? 1;

  const buildImageUrl = (
    url: string,
    { width, height }: { width?: number; height?: number }
  ): string => {
    const params = new URLSearchParams({
      format: 'webp',
      quality: '90',
      ...(width && { width: width.toString() }),
      ...(height && { height: height.toString() }),
    });

    return `${url}?${params}`;
  };

  const mobileUrl = mobileImage
    ? buildImageUrl(mobileImage.url, { width: 900, height: 1000 })
    : undefined;

  const desktopUrl = buildImageUrl(src.url, { height: 800 });

  return (
    <picture className="tw-block tw-h-full tw-min-h-full tw-min-w-full tw-overflow-hidden">
      {mobileUrl && (
        <source
          type="image/webp"
          media={`(max-width:${752 * devicePixelRatio}px)`}
          srcSet={mobileUrl}
        />
      )}
      <img
        src={desktopUrl}
        className="tw-h-full tw-min-h-full tw-min-w-full tw-object-cover"
        loading="eager"
        fetchPriority="high"
        alt={src.altText || 'hero image'}
        sizes={isSplit ? '(max-width: 1408px) 100vw, 50vw' : '100vw'}
      />
    </picture>
  );
};

export const HeroMedia = ({
  alternativeText,
  autoPlay = true,
  className,
  darkFont = false,
  hasQBrickPlayButton,
  image,
  isSplit,
  mobileImage,
  muted = true,
  playButtonLabel,
  video,
  videoPoster,
  videoConfig = '',
}: IHeroMedia) => {
  const [videoError, setVideoError] = useState(false);
  const [playVideo, setPlayVideo] = useState(autoPlay);
  const videoRef = useRef<HTMLVideoElement>(null);

  return (
    <div className={classNames(className, '')}>
      {image && (!video || videoError || hasQBrickPlayButton) ? (
        <HeroImageComponent
          isSplit={isSplit}
          src={{ ...image, altText: alternativeText || image.altText }}
          mobileImage={mobileImage}
        />
      ) : null}
      {hasQBrickPlayButton && video ? (
        <div className="tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2">
          <VideoButton
            darkFont={darkFont}
            playButtonLabel={playButtonLabel}
            videoSource={video.videoSource}
            videoConfig={videoConfig}
          />
        </div>
      ) : null}
      {video && !hasQBrickPlayButton && !videoError ? (
        <div className="tw-relative tw-h-full tw-w-full">
          <video
            ref={videoRef}
            preload="metadata"
            loop
            controls={playVideo && !autoPlay}
            playsInline
            autoPlay={autoPlay}
            muted={muted}
            className="tw-h-full tw-w-full tw-object-cover tw-object-center"
            onError={() => setVideoError(true)}
            poster={
              autoPlay
                ? undefined
                : videoPoster?.url ?? image?.url ?? video.poster?.url
            }
          >
            <source
              src={`${video.videoSource}${autoPlay ? '#t=0.1' : ''}`}
              type="video/mp4"
            />
            {/* Fallback image */}
            {image ? (
              <HeroImageComponent
                isSplit={isSplit}
                src={image}
                mobileImage={mobileImage}
              />
            ) : null}
          </video>
          {!playVideo && !autoPlay ? (
            <div className="tw-absolute tw-left-1/2 tw-top-1/2 tw-z-10 -tw-translate-x-1/2 -tw-translate-y-1/2">
              <VideoButton
                darkFont={darkFont}
                playButtonLabel={playButtonLabel}
                onClick={() => {
                  videoRef.current?.play();
                  setPlayVideo(!playVideo);
                }}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
